import { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaDollarSign } from 'react-icons/fa';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { useColumnVisibility, ColumnPicker } from '../../components/ColumnPicker';

const empty = { userId: '', clientId: '', project: '', rate: '', terms: '45 days payment', active: true };

export default function AdminConsultantRates() {
  const { user } = useAuth();
  const isSuperAdmin = user?.permissions?.includes('manage_consultant_rates');
  const [rates, setRates]       = useState([]);
  const [users, setUsers]       = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]         = useState(empty);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.getConsultantRates(), api.getUsers(), api.getClients()])
      .then(([r, u, c]) => { setRates(r); setUsers(u); setClients(c); })
      .catch(() => setError('Failed to load data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const openNew = () => { setForm(empty); setEditingId(null); setShowForm(true); setError(''); };
  const openEdit = (r) => {
    setForm({
      userId: String(r.user_id), clientId: String(r.client_id),
      project: r.project || '', rate: String(r.rate),
      terms: r.terms || '45 days payment', active: r.active,
    });
    setEditingId(r.id);
    setShowForm(true);
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const body = { userId: parseInt(form.userId), clientId: parseInt(form.clientId), project: form.project, rate: parseFloat(form.rate), terms: form.terms, active: form.active };
    try {
      if (editingId) {
        await api.updateConsultantRate(editingId, body);
        setSuccess('Rate updated.');
      } else {
        await api.createConsultantRate(body);
        setSuccess('Rate created.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteConsultantRate(id);
      setSuccess('Rate deleted.');
      setConfirmDelete(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const activeEmployees = users.filter(u => u.active !== false);
  const activeClients   = clients.filter(c => c.is_active !== false);

  const COLS = [
    { key: 'project', label: 'Project' },
    { key: 'terms',   label: 'Terms'   },
    { key: 'status',  label: 'Status'  },
  ];
  const { visible, toggle, show } = useColumnVisibility(COLS, 'cols_rates');

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0c1b4d] flex items-center justify-center">
              <FaDollarSign className="text-[#D4AF37]" size={16} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#0c1b4d]">Consultant Rates</h1>
              <p className="text-xs text-gray-500">{rates.length} rate{rates.length !== 1 ? 's' : ''} configured</p>
            </div>
          </div>
          {isSuperAdmin && (
            <button onClick={openNew}
              className="flex items-center gap-2 px-4 py-2 bg-[#0c1b4d] text-white rounded-lg text-sm font-semibold hover:bg-blue-900 transition-colors">
              <FaPlus size={12} /> Add Rate
            </button>
          )}
        </div>

        {error   && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        {success && <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{success}</div>}

        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading…</div>
        ) : rates.length === 0 ? (
          <div className="text-center py-16 text-gray-400">No rates configured yet.</div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <ColumnPicker columns={COLS} visible={visible} onToggle={toggle} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Consultant</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Client</th>
                    {show('project') && <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Project</th>}
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 whitespace-nowrap">Rate/hr</th>
                    {show('terms')  && <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Terms</th>}
                    {show('status') && <th className="px-4 py-3 text-center font-semibold text-gray-600 whitespace-nowrap">Status</th>}
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rates.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                        {r.consultant_name}
                        <span className="ml-1 text-xs text-gray-400">#{r.employee_id}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{r.client_name}</td>
                      {show('project') && <td className="px-4 py-3 text-gray-600">{r.project || '—'}</td>}
                      <td className="px-4 py-3 text-right font-semibold text-gray-800">${Number(r.rate).toFixed(2)}</td>
                      {show('terms')  && <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{r.terms}</td>}
                      {show('status') && <td className="px-4 py-3 text-center">
                        {r.active
                          ? <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Active</span>
                          : <span className="px-2 py-0.5 bg-gray-100 text-gray-400 rounded-full text-xs">Inactive</span>}
                      </td>}
                      <td className="px-4 py-3 text-right">
                        {isSuperAdmin && (
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => openEdit(r)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit"><FaEdit size={13} /></button>
                            <button onClick={() => setConfirmDelete(r)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Delete"><FaTrash size={13} /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-bold text-[#0c1b4d]">{editingId ? 'Edit Rate' : 'Add Rate'}</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><FaTimes size={14} /></button>
            </div>
            <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
              {error && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Consultant *</label>
                <select value={form.userId} onChange={e => set('userId', e.target.value)} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]">
                  <option value="">Select employee…</option>
                  {activeEmployees.map(u => (
                    <option key={u.id} value={u.id}>{u.name} (#{u.employee_id})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Client *</label>
                <select value={form.clientId} onChange={e => set('clientId', e.target.value)} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]">
                  <option value="">Select client…</option>
                  {activeClients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Project / Engagement</label>
                <input value={form.project} onChange={e => set('project', e.target.value)} placeholder="e.g. ERP Rollout Q2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Hourly Rate ($) *</label>
                  <input type="number" min="0" step="0.01" value={form.rate} onChange={e => set('rate', e.target.value)} required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Terms</label>
                  <input value={form.terms} onChange={e => set('terms', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]" />
                </div>
              </div>

              {editingId && (
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#0c1b4d] focus:ring-[#0c1b4d]" />
                  Active
                </label>
              )}

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0c1b4d] text-white rounded-lg text-sm font-semibold hover:bg-blue-900 transition-colors disabled:opacity-60">
                  <FaSave size={13} /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm px-6 py-5">
            <h3 className="text-base font-bold text-gray-800 mb-2">Delete Rate?</h3>
            <p className="text-sm text-gray-600 mb-5">
              Remove the rate for <strong>{confirmDelete.consultant_name}</strong> → <strong>{confirmDelete.client_name}</strong>?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete.id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
